import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Loader2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { trackCoverUrl, fmtTime } from "../lib/api";

export const PlayerBar = () => {
  const { current, playing, loading, progress, duration, toggle, seek, close } = usePlayer();

  const bars = useMemo(() => {
    if (!current) return [];
    let s = 7;
    for (const c of current.track_id) s += c.charCodeAt(0);
    const arr = [];
    let x = s;
    for (let i = 0; i < 52; i++) {
      x = (x * 9301 + 49297) % 233280;
      arr.push(8 + (x / 233280) * 30);
    }
    return arr;
  }, [current]);

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          data-testid="player-bar"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-50 border-t border-white/[0.08] bg-black/70 backdrop-blur-xl"
        >
          <input
            type="range"
            min={0}
            max={1000}
            value={duration ? Math.round((progress / duration) * 1000) : 0}
            onChange={(e) => seek(Number(e.target.value) / 1000)}
            data-testid="player-seek"
            className="seek-bar absolute -top-[2px] left-0 w-full cursor-pointer"
            aria-label="Progression"
          />
          <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3 sm:px-8">
            <img
              src={trackCoverUrl(current)}
              alt={current.title}
              className="h-11 w-11 border border-white/10 object-cover"
            />
            <div className="min-w-0">
              <p data-testid="player-title" className="truncate font-display text-sm font-medium">{current.title}</p>
              <p className="truncate text-[11px] uppercase tracking-[0.2em] text-zinc-500">{current.artist}</p>
            </div>
            <button
              onClick={toggle}
              data-testid="player-toggle"
              className="ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black transition-transform hover:scale-105"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : playing ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="ml-0.5 h-4 w-4" />
              )}
            </button>
            <div className={`hidden h-10 flex-1 items-end justify-center gap-[3px] sm:flex ${playing ? "" : "eq-paused"}`} data-testid="player-waveform">
              {bars.map((h, i) => (
                <span
                  key={i}
                  className="eq-bar w-[3px] bg-amber-500/70"
                  style={{ height: `${h}px`, animationDelay: `${(i % 9) * 0.08}s`, animationDuration: `${0.7 + (i % 5) * 0.12}s` }}
                />
              ))}
            </div>
            <span data-testid="player-time" className="hidden text-xs tabular-nums text-zinc-400 sm:block">
              {fmtTime(progress)} / {fmtTime(duration)}
            </span>
            <button
              onClick={close}
              data-testid="player-close"
              className="flex h-9 w-9 items-center justify-center text-zinc-500 transition-colors hover:text-white"
              title="Fermer le lecteur"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
